import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, ClipboardList, Gauge, ListChecks, ThumbsUp } from 'lucide-react';
import { toast } from 'sonner';
import { Skeleton } from '../../ui/skeleton';
import { KpiStrip } from '../KpiStrip';
import { downloadBlob } from '../qualityConfig';
import { ReportByKind, ReportTrendChart } from './ReportCharts';
import { defaultFilters, filtersToParams, ReportFilters } from './ReportFilters';
import { ReportByUserTable, ReportFeedbackList } from './ReportTables';

const kpis = (s) => [
    { id: 'report-tasks', label: 'Tasks assigned', value: s.total, icon: ClipboardList, tone: 'text-indigo-500', hint: `${s.open} still open` },
    { id: 'report-completion', label: 'Completion rate', value: s.completion_rate === null ? null : `${s.completion_rate}%`, icon: CheckCircle2, tone: 'text-emerald-500', hint: `${s.completed} done · ${s.missed} missed` },
    { id: 'report-avg', label: 'Avg task score', value: s.avg_score, icon: Gauge, tone: 'text-violet-500', hint: 'Missed tasks count as 0' },
    { id: 'report-sims', label: 'Simulations done', value: `${s.sims_completed}/${s.sims_assigned}`, icon: ListChecks, tone: 'text-amber-500' },
    { id: 'report-helpful', label: 'Found helpful', value: s.helpful_pct === null ? null : `${s.helpful_pct}%`, icon: ThumbsUp, tone: 'text-fuchsia-500', hint: `${s.feedback_count} response${s.feedback_count === 1 ? '' : 's'}` },
];

export const ReportsTab = ({ api, team, refreshKey, onOpenUser }) => {
    const [filters, setFilters] = useState(defaultFilters);
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const params = useMemo(() => filtersToParams(filters), [filters]);
    const ready = filters.period !== 'custom' || (filters.start && filters.end);

    useEffect(() => {
        if (!ready) return;
        setLoading(true);
        api.get('/quality-tasks/admin/report', { params })
            .then(({ data }) => setReport(data))
            .catch((e) => toast.error(e.response?.data?.detail || 'Could not load the report'))
            .finally(() => setLoading(false));
    }, [api, params, ready, refreshKey]);

    const exportCsv = async () => {
        setExporting(true);
        try {
            const res = await api.get('/quality-tasks/admin/report/export', { params, responseType: 'blob' });
            downloadBlob(res.data, `quality_tasks_${(report?.period.label || 'report').replace(/[^A-Za-z0-9]+/g, '_')}.csv`);
            toast.success('Report downloaded');
        } catch {
            toast.error('Could not export the report');
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="quality-reports-tab">
            <ReportFilters filters={filters} setFilters={setFilters} team={team} onExport={exportCsv} exporting={exporting || !ready} />
            {!ready && <p className="text-sm text-muted-foreground">Pick a start and end date to see the report.</p>}
            {ready && (loading && !report ? <Skeleton className="h-64 w-full rounded-2xl" /> : report && (
                <div className={`space-y-6 transition-opacity ${loading ? 'opacity-60' : ''}`}>
                    <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground" data-testid="report-period-label">{report.period.label} · tasks assigned in this period</p>
                    <KpiStrip items={kpis(report.summary)} testId="report-kpis" />
                    <div className="grid gap-6 lg:grid-cols-12">
                        <ReportTrendChart data={report.trend} bucket={report.period.bucket} />
                        <ReportByKind rows={report.by_kind} />
                    </div>
                    <ReportByUserTable rows={report.by_user} onOpenUser={onOpenUser} />
                    <ReportFeedbackList items={report.feedback} />
                </div>
            ))}
        </div>
    );
};
